// ManagerConsistency.js

import React, { Component } from 'react';
import { connect } from 'react-redux';
import ReactEcharts from 'echarts-for-react';
import Select from 'react-select';
import i18n from '../../../i18n';
import './RollingHorizons.css';
import PerformanceMetricsTable from './PerformanceMetricsTable';
import './ManagerConsistency.css';

// Constants for mapping rolling windows
const ROLL_WINDOW_TO_DATE_FIELD = {
  1: 'yrs1NAVDate',
  3: 'yrs3NAVDate',
  5: 'yrs5NAVDate',
  10: 'yrs10NAVDate',
};

const ROLL_WINDOW_TO_DATA_FIELD = {
  1: 'yrs1NAV',
  3: 'yrs3NAV',
  5: 'yrs5NAV',
  10: 'yrs10NAV',
};

/**
 * Formats the fund name to include ticker.
 */
function getFormattedFundName(fund) {
  return fund ? `${fund.name} (${fund.ticker})` : 'Fund Name (Ticker)';
}

/**
 * Formats data values based on type.
 */
function dataFormatter(value, type) {
  if (type === 'axisPercentage') {
    // For axis labels, no decimals
    return typeof value === 'number' ? `${value.toFixed(0)}%` : `${value}`;
  } else if (type === 'percentage') {
    // Two decimals for data points
    return typeof value === 'number' ? `${value.toFixed(2)}%` : 'N/A';
  } else if (type === 'return') {
    // Two decimals for return
    return typeof value === 'number' ? `${value.toFixed(2)}%` : 'N/A';
  } else {
    // Default formatting
    return typeof value === 'number' ? value.toFixed(2) : 'N/A'; // Default for other types of data
  }
}

/**
 * Calculates table data based on selected Y and X funds.
 * Converts returns from decimals to percentages and rounds them to two decimal places.
 */
function calculateTableData(data, selectedYFundTicker, selectedXFundTicker, rollWindow) {
  const yFund = data.find((fund) => fund.ticker === selectedYFundTicker);
  const xFund = data.find((fund) => fund.ticker === selectedXFundTicker);

  if (!yFund || !xFund) {
    return { name: '', numObservations: 0, outperform: 0, outperformPercent: 0 };
  }

  const node = 'managerConsistency';
  const yDataField = `yrs${rollWindow}NAV`;
  const xDataField = `yrs${rollWindow}NAV`;

  const yReturns = yFund.rollingHorizons[node][yDataField] || [];
  const xReturns = xFund.rollingHorizons[node][xDataField] || [];

  // Ensure both funds have the same number of data points
  const minLength = Math.min(yReturns.length, xReturns.length);
  const filteredYReturns = yReturns.slice(0, minLength);
  const filteredXReturns = xReturns.slice(0, minLength);

  // Calculate outperform instances (Y > X)
  let outperform = 0;
  let numObservations = 0;

  for (let i = 0; i < minLength; i++) {
    const y = parseFloat(filteredYReturns[i]) * 100; // Convert to %
    const x = parseFloat(filteredXReturns[i]) * 100; // Convert to %
    if (!isNaN(x) && !isNaN(y)) {
      numObservations += 1;
      if (y > x) {
        outperform += 1;
      }
    }
  }

  const outperformPercent = numObservations > 0 ? (outperform / numObservations) * 100 : 0;

  return {
    name: getFormattedFundName(yFund),
    numObservations,
    outperform,
    outperformPercent, // Keep as number
  };
}

/**
 * Determines the default X-Axis fund based on the selected Y-Axis fund.
 * Chooses the first available fund that is not the selected Y-Axis fund.
 */
function getDefaultXFund(data, selectedYFundTicker) {
  const defaultXFund = data.find((fund) => fund.ticker !== selectedYFundTicker);
  return defaultXFund ? defaultXFund.ticker : null;
}

/**
 * Prepares the chart data for rendering.
 */
function chartData(data, selectedYFundTicker, selectedXFundTicker, rollWindow) {
  // Find Y and X funds
  const yFund = data.find((fund) => fund.ticker === selectedYFundTicker);
  const xFund = data.find((fund) => fund.ticker === selectedXFundTicker);

  if (!yFund || !xFund) {
    console.error('Selected Y Fund or X Fund is missing.');
    return {};
  }

  const node = 'managerConsistency';
  const yDataField = `yrs${rollWindow}NAV`;
  const yDateField = `yrs${rollWindow}NAVDate`;
  const xDataField = `yrs${rollWindow}NAV`;
  const xDateField = `yrs${rollWindow}NAVDate`;

  const yReturns = yFund.rollingHorizons[node][yDataField] || [];
  const yDates = yFund.rollingHorizons[node][yDateField] || [];
  const xReturns = xFund.rollingHorizons[node][xDataField] || [];
  const xDates = xFund.rollingHorizons[node][xDateField] || [];

  // Ensure both funds have the same number of data points
  const minLength = Math.min(yReturns.length, xReturns.length, yDates.length, xDates.length);
  const filteredYReturns = yReturns.slice(0, minLength);
  const filteredXReturns = xReturns.slice(0, minLength);
  const filteredDates = yDates.slice(0, minLength); // Assuming dates align

  // Prepare scatter data: array of objects with value and date
  const scatterData = [];
  for (let i = 0; i < minLength; i++) {
    const x = parseFloat(filteredXReturns[i]) * 100; // Convert to %
    const y = parseFloat(filteredYReturns[i]) * 100; // Convert to %
    const date = filteredDates[i]; // 'MM/DD/YYYY'

    if (!isNaN(x) && !isNaN(y)) {
      scatterData.push({
        value: [x, y],
        date: date,
      });
    }
  }

  if (scatterData.length === 0) {
    console.warn('No valid scatter data points found.');
    return {};
  }

  // Calculate min and max returns based on converted data
  const allX = scatterData.map((point) => point.value[0]);
  const allY = scatterData.map((point) => point.value[1]);
  const minReturn = Math.min(...allX, ...allY);
  const maxReturn = Math.max(...allX, ...allY);

  // Define the 45-degree line from (min, min) to (max, max)
  const lineData = [
    [minReturn - 2, minReturn - 2], // Adding 2% padding
    [maxReturn + 2, maxReturn + 2],
  ];

  // Define labels for selected funds
  const yFundLabel = getFormattedFundName(yFund);
  const xFundLabel = getFormattedFundName(xFund);

  return {
    // Disable the legend
    legend: {
      show: false,
    },
    title: {
      text: '', // No main title; handled in component render
      left: 'center',
      top: '5%',
      textStyle: {
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 14,
        color: '#8C959B',
      },
    },
    tooltip: {
      trigger: 'item',
      formatter: function (params) {
        const x = params.value[0];
        const y = params.value[1];
        const date = new Date(params.data.date).toLocaleDateString(); // Format date without timestamp
        const position = y > x ? 'Above' : y < x ? 'Below' : 'On';

        return `
          <strong>Date:</strong> ${date}<br/>
          <strong>${xFundLabel}:</strong> ${dataFormatter(x, 'return')}<br/>
          <strong>${yFundLabel}:</strong> ${dataFormatter(y, 'return')}<br/>
          <strong>Position Relative to 45° Line:</strong> ${position}
        `;
      },
      backgroundColor: '#FFF',
      borderWidth: 1,
      borderColor: '#050139',
      borderRadius: 0,
      textStyle: {
        color: '#5B6770',
        align: 'left',
      },
    },
    dataZoom: [], // No dataZoom for scatter plot
    xAxis: {
      type: 'value',
      name: xFundLabel,
      nameLocation: 'middle',
      nameGap: 40, // Increased for better spacing
      nameTextStyle: {
        fontSize: 12, // Adjust this value to make the font larger
        color: '#050139', // Updated color
        fontWeight: 'bold', // Bold the text
      },
      min: minReturn - 2, // Adding 2% padding
      max: maxReturn + 2,
      interval: 4, // Increased interval to reduce label density
      axisLabel: {
        show: true,
        interval: 1, // Show label every interval
        fontSize: 12, // Smaller font size
        color: '#050139',
        formatter: function (value) {
          return dataFormatter(value, 'axisPercentage'); // No decimal places
        },
        rotate: 45, // Rotate labels to 45 degrees
        textStyle: {
          fontWeight: 'bold', // Ensure fontWeight is within textStyle
        },
      },
      axisLine: {
        lineStyle: {
          color: '#050139', // Match axis label color
        },
      },
      axisTick: {
        lineStyle: {
          color: '#050139',
        },
      },
      splitLine: {
        show: false, // Remove horizontal grid lines
      },
      splitArea: {
        show: false, // Remove background split areas
      },
    },
    yAxis: {
      type: 'value',
      name: yFundLabel,
      nameLocation: 'middle',
      nameGap: 60, // Increased for better spacing
      nameTextStyle: {
        fontSize: 14, // Adjust this value to make the font larger
        color: '#050139', // Updated color
        fontWeight: 'bold', // Bold the text
      },
      min: minReturn - 2, // Adding 2% padding
      max: maxReturn + 2,
      interval: 4, // Increased interval to reduce label density
      axisLabel: {
        show: true,
        interval: 1, // Show label every interval
        fontSize: 12, // Smaller font size
        color: '#050139',
        fontWeight: 'bold', // Bold the text
        formatter: function (value) {
          return dataFormatter(value, 'axisPercentage'); // No decimal places
        },
        textStyle: {
          fontWeight: 'bold', // Ensure fontWeight is within textStyle
        },
      },
      axisLine: {
        lineStyle: {
          color: '#050139', // Match axis label color
        },
      },
      axisTick: {
        lineStyle: {
          color: '#050139',
        },
      },
      splitLine: {
        show: false, // Remove horizontal grid lines
      },
      splitArea: {
        show: false, // Remove background split areas
      },
    },
    graphic: {
      type: 'image',
      id: 'logo',
      right: 5, // Place logo at upper right
      top: 5, // Adjusted top margin
      style: {
        image: 'https://www.wisdomtree.com/-/media/us-media-files/home/wt-logo-navy-2024.png',
        width: 100,
        height: 40,
      },
    },
    series: [
      {
        name: 'Returns Scatter',
        type: 'scatter',
        symbolSize: 8,
        data: scatterData.map((point, index) => {
          // Check if it's the last data point
          const isLast = index === scatterData.length - 1;
          return {
            value: point.value,
            date: point.date,
            itemStyle: {
              color: isLast ? 'red' : '#050139', // Red for the last point, default color otherwise
            },
          };
        }),
      },
      {
        name: '45° Line',
        type: 'line',
        data: lineData, // Data already in percentages and rounded
        symbol: 'none',
        lineStyle: {
          color: '#5470C6',
          width: 1,
          type: 'solid', // Changed to solid for consistency
        },
      },
    ],
  };
}

class ManagerConsistency extends Component {
  state = {
    selectedYFund: null, // Selected Y-axis fund ticker
    selectedXFund: null, // Selected X-axis fund ticker
    rollWindow: 3, // Default rolling window (3Y)
    chartOptions: {},
    error: '', // For error messages
    tableData: {
      name: '',
      numObservations: 0,
      outperform: 0,
      outperformPercent: 0, // Keep as number
    },
  };

  componentDidMount() {
    const { data } = this.props;

    if (!data || data.length < 2) {
      this.setState({
        error: 'Insufficient fund data available. At least two funds are required.',
      });
      return;
    }

    // Filter funds that have the 'managerConsistency' node
    const validFunds = data.filter(
      (fund) =>
        fund.rollingHorizons &&
        fund.rollingHorizons['managerConsistency'] &&
        Object.keys(ROLL_WINDOW_TO_DATA_FIELD).some(
          (window) =>
            fund.rollingHorizons['managerConsistency'][`yrs${window}NAV`] &&
            fund.rollingHorizons['managerConsistency'][`yrs${window}NAVDate`]
        )
    );

    if (validFunds.length < 2) {
      this.setState({
        error:
          'Insufficient fund data with manager consistency available. At least two funds with the required data are needed.',
      });
      return;
    }

    // Initialize selectedYFund to the first valid fund
    const selectedYFund = validFunds[0].ticker;
    // Initialize selectedXFund to the second valid fund
    const selectedXFund = validFunds[1].ticker;

    const rollWindow = this.state.rollWindow; // Default rolling window
    const chartOptions = chartData(data, selectedYFund, selectedXFund, rollWindow);

    // Calculate initial table data
    const tableData = calculateTableData(data, selectedYFund, selectedXFund, rollWindow);
    console.log('Initial Table Data:', tableData); // Debugging

    this.setState({
      selectedYFund,
      selectedXFund,
      chartOptions,
      tableData,
      error: '',
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Reinitialize chart if data, selectedYFund, selectedXFund, or rollWindow changes
    if (
      prevProps.data !== this.props.data ||
      prevState.selectedYFund !== this.state.selectedYFund ||
      prevState.selectedXFund !== this.state.selectedXFund ||
      prevState.rollWindow !== this.state.rollWindow
    ) {
      this.initializeChart();
    }
  }

  /**
   * Initializes or updates the chart based on current selections.
   */
  initializeChart = () => {
    const { data } = this.props;
    const { selectedYFund, selectedXFund, rollWindow } = this.state;

    // Filter funds that have the 'managerConsistency' node
    const validFunds = data.filter(
      (fund) =>
        fund.rollingHorizons &&
        fund.rollingHorizons['managerConsistency'] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAV`] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAVDate`]
    );

    if (validFunds.length < 2) {
      this.setState({
        error:
          'Insufficient fund data with manager consistency available. At least two funds with the required data are needed.',
      });
      return;
    }

    // Ensure both funds are selected
    if (!selectedYFund || !selectedXFund) {
      this.setState({ error: 'Please select both Y and X funds to view the chart.' });
      return;
    }

    // Ensure Y and X funds are different
    if (selectedYFund === selectedXFund) {
      // Automatically set X to a different fund
      const defaultXFund = getDefaultXFund(validFunds, selectedYFund);
      if (defaultXFund) {
        this.setState({ selectedXFund: defaultXFund, error: '' }, () => {
          this.initializeChart(); // Reinitialize chart with updated X fund
        });
      } else {
        this.setState({ error: 'No available funds to set as X-axis fund.' });
      }
      return;
    }

    const chartOptions = chartData(data, selectedYFund, selectedXFund, rollWindow);

    // Calculate table data
    const tableData = calculateTableData(data, selectedYFund, selectedXFund, rollWindow);

    this.setState({
      chartOptions,
      tableData,
      error: '', // Clear any existing errors
    });
  };

  /**
   * Determines available rolling windows based on data availability.
   */
  getAvailableRollingWindows = () => {
    const { data } = this.props;
    const { selectedYFund, selectedXFund } = this.state;

    if (!selectedYFund || !selectedXFund) {
      return [];
    }

    const yFund = data.find((fund) => fund.ticker === selectedYFund);
    const xFund = data.find((fund) => fund.ticker === selectedXFund);

    if (!yFund || !xFund) {
      return [];
    }

    const possibleWindows = [1, 3, 5, 10];
    const availableWindows = possibleWindows.filter((window) => {
      const yHasData =
        yFund.rollingHorizons &&
        yFund.rollingHorizons['managerConsistency'] &&
        yFund.rollingHorizons['managerConsistency'][`yrs${window}NAV`] &&
        yFund.rollingHorizons['managerConsistency'][`yrs${window}NAVDate`];

      const xHasData =
        xFund.rollingHorizons &&
        xFund.rollingHorizons['managerConsistency'] &&
        xFund.rollingHorizons['managerConsistency'][`yrs${window}NAV`] &&
        xFund.rollingHorizons['managerConsistency'][`yrs${window}NAVDate`];

      return yHasData && xHasData;
    });

    return availableWindows;
  };

  /**
   * Handles Y-axis fund selection.
   */
  handleYFundChange = (selectedOption) => {
    const { data } = this.props;
    const selectedYFund = selectedOption ? selectedOption.value : null;
    const { selectedXFund, rollWindow } = this.state;

    if (!selectedYFund) {
      this.setState({ error: 'Please select a Y-axis fund.' });
      return;
    }

    // Filter valid funds based on the selected rolling window
    const validFunds = data.filter(
      (fund) =>
        fund.rollingHorizons &&
        fund.rollingHorizons['managerConsistency'] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAV`] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAVDate`]
    );

    // If the new Y fund is the same as current X fund, set X to default
    let updatedXFund = selectedXFund;
    if (selectedYFund === selectedXFund) {
      updatedXFund = getDefaultXFund(validFunds, selectedYFund);
    }

    // If no default X fund is found (only one fund), set error
    if (!updatedXFund && validFunds.length >= 2) {
      updatedXFund = validFunds.find((fund) => fund.ticker !== selectedYFund)?.ticker;
    }

    if (!updatedXFund && validFunds.length < 2) {
      this.setState({ error: 'Insufficient funds to set as X-axis fund.' });
      return;
    }

    this.setState(
      {
        selectedYFund,
        selectedXFund: updatedXFund,
        error: '',
      },
      () => {
        this.initializeChart();
      }
    );
  };

  /**
   * Handles X-axis fund selection.
   */
  handleXFundChange = (selectedOption) => {
    const { data } = this.props;
    const selectedXFund = selectedOption ? selectedOption.value : null;
    const { selectedYFund, rollWindow } = this.state;

    if (!selectedXFund) {
      this.setState({ error: 'Please select an X-axis fund.' });
      return;
    }

    // Ensure X fund is different from Y fund
    if (selectedXFund === selectedYFund) {
      // Automatically set Y to a different fund
      const validFunds = data.filter(
        (fund) =>
          fund.rollingHorizons &&
          fund.rollingHorizons['managerConsistency'] &&
          fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAV`] &&
          fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAVDate`]
      );
      const defaultYFund = getDefaultXFund(validFunds, selectedXFund); // Reusing the same function
      if (defaultYFund) {
        this.setState(
          {
            selectedYFund: defaultYFund,
            selectedXFund,
            error: '',
          },
          () => {
            this.initializeChart();
          }
        );
      } else {
        this.setState({ error: 'No available funds to set as Y-axis fund.' });
      }
      return;
    }

    this.setState(
      {
        selectedXFund,
        error: '',
      },
      () => {
        this.initializeChart();
      }
    );
  };

  /**
   * Handles rolling window button clicks.
   */
  handleRollWindowChange = (window) => {
    const { data } = this.props;
    const { selectedYFund, selectedXFund } = this.state;

    if (!selectedYFund || !selectedXFund) {
      this.setState({
        error: 'Please select both Y and X funds before changing the rolling window.',
      });
      return;
    }

    // Update rolling window
    this.setState({ rollWindow: window }, () => {
      this.initializeChart();
    });
  };

  render() {
    const {
      selectedYFund,
      selectedXFund,
      rollWindow,
      chartOptions,
      error,
      tableData,
    } = this.state;
    const { data } = this.props;

    // Find Y and X funds for label formatting
    const yFund = data.find((fund) => fund.ticker === selectedYFund);
    const xFund = data.find((fund) => fund.ticker === selectedXFund);

    // Define yFundLabel and xFundLabel
    const yFundLabel = yFund ? `${yFund.name} (${yFund.ticker})` : '';
    const xFundLabel = xFund ? `${xFund.name} (${xFund.ticker})` : '';

    // Filter valid funds based on the selected rolling window
    const validFunds = data.filter(
      (fund) =>
        fund.rollingHorizons &&
        fund.rollingHorizons['managerConsistency'] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAV`] &&
        fund.rollingHorizons['managerConsistency'][`yrs${rollWindow}NAVDate`]
    );

    // Prepare options for the Y-axis fund select (include all valid funds)
    const yFundOptions =
      validFunds && validFunds.length > 0
        ? validFunds.map((fund) => ({
          value: fund.ticker,
          label: getFormattedFundName(fund),
        }))
        : [];

    // Prepare options for the X-axis fund select (exclude the selected Y fund)
    const xFundOptions =
      validFunds && validFunds.length > 0
        ? validFunds
          .filter((fund) => fund.ticker !== selectedYFund)
          .map((fund) => ({
            value: fund.ticker,
            label: getFormattedFundName(fund),
          }))
        : [];

    // Determine available rolling windows
    const availableRollingWindows = this.getAvailableRollingWindows();

    return (
      <div className='manager-consistency'>
        {/* Main Title */}
        <div className='chartHeader'>{i18n.t('Manager Consistency')}</div>

        {/* Dynamic Rolling Window Title */}
        {rollWindow && (
          <div
            className='rolling-window-title'
            style={{
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#050139',
            }}
          >
            Manager Consistency: Annualized Total Returns (Quarterly Observations) {rollWindow}Y
          </div>
        )}

        {/* Selected Funds Display Below the Chart Header */}
        {selectedYFund && selectedXFund && (
          <div
            className='selected-funds-display'
            style={{
              textAlign: 'center',
              marginBottom: '20px',
              fontSize: '16px',
              fontWeight: 'bold',
              color: '#050139', // Ensure the text color matches the theme
            }}
          >
            <strong>{yFundLabel}</strong> vs <strong>{xFundLabel}</strong>
          </div>
        )}

        {/* Row 1: Selects and Rolling Window Buttons */}
        <div
          className='row'
          style={{
            marginBottom: '30px',
            marginTop: '40px',
            alignItems: 'center',
          }}
        >
          {/* Y-Axis Fund Select */}
          <div className='col-md-4' style={{ paddingRight: '10px' }}>
            <Select
              options={yFundOptions}
              value={
                selectedYFund
                  ? {
                    value: selectedYFund,
                    label: getFormattedFundName(
                      data.find((fund) => fund.ticker === selectedYFund)
                    ),
                  }
                  : null
              }
              onChange={this.handleYFundChange}
              placeholder='Select Y-Axis Fund'
              isClearable={false} // Y-Axis fund should always have a selection
              className='y-fund-select'
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}

            />
          </div>

          {/* X-Axis Fund Select */}
          <div className='col-md-4' style={{ paddingRight: '10px' }}>
            <Select
              options={xFundOptions}
              value={
                selectedXFund
                  ? {
                    value: selectedXFund,
                    label: getFormattedFundName(
                      data.find((fund) => fund.ticker === selectedXFund)
                    ),
                  }
                  : null
              }
              onChange={this.handleXFundChange}
              placeholder='Select X-Axis Fund'
              isClearable={false} // X-Axis fund should always have a selection
              className='x-fund-select'
              styles={{
                control: (provided) => ({
                  ...provided,
                  height: '38px',
                  borderRadius: '0px',
                }),
                singleValue: (provided) => ({
                  ...provided,
                  textAlign: 'center',
                }),
              }}

            />
          </div>

          {/* Rolling Window Buttons */}
          <div
            className='col-md-4'
            style={{
              display: 'flex',
              justifyContent: 'right',
              gap: '0px',
            }}
          >
            {availableRollingWindows.map((window) => (
              <button
                key={window}
                className={`mgrConsbutton ${rollWindow === window
                  ? 'mgrConsbutton-active'
                  : 'mgrConsbutton-inactive'
                  }`}
                onClick={() => this.handleRollWindowChange(window)}
                aria-label={`Select ${window} Year Rolling Window`}
              >
                {window}Y
              </button>
            ))}
          </div>
        </div>

        {/* Display error message if any */}
        {error && (
          <div className='row' style={{ marginBottom: '10px' }}>
            <div
              className='col-md-12'
              style={{ color: 'red', textAlign: 'center' }}
            >
              {error}
            </div>
          </div>
        )}

        {/* Chart and Tables */}
        {!error && (
          <>
            {/* Chart */}
            <div className='chartContainer'>
              {chartOptions && Object.keys(chartOptions).length > 0 ? (
                <ReactEcharts
                  option={chartOptions}
                  ref={(e) => (this.echartsRef = e)}
                  theme='default'
                  notMerge={true}
                  style={{ height: 600, width: '100%' }} // Make the chart responsive
                  onEvents={{
                    datazoom: (params) => {
                      // Handle data zoom events if necessary
                      console.log('Data zoom event:', params);
                    },
                  }}
                />
              ) : (
                <div
                  style={{
                    textAlign: 'center',
                    color: '#8C959B',
                    padding: '20px',
                  }}
                >
                  Select Y and X funds to view the chart.
                </div>
              )}
            </div>

            {/* Performance Metrics Table */}
            {chartOptions && Object.keys(chartOptions).length > 0 && (
              <PerformanceMetricsTable tableData={tableData} />
            )}
          </>
        )}
      </div>
    );
  }
}


// **Redux Mapping**
function mapStateToProps(state) {
  return {
    data: state.fundData.data,
  };
}

export default connect(mapStateToProps, {})(ManagerConsistency);
